// GA 이벤트 트래킹 유틸리티 함수들

// Window 객체에 gtag와 dataLayer 타입 선언
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export interface GAEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: any;
}

// GA 추적 ID 상수
export const GA_TRACKING_ID = "G-RQ5L241HHP";

/**
 * gtag이 로드되었는지 확인합니다.
 */
export const isGtagLoaded = (): boolean => {
  return (
    typeof window !== "undefined" &&
    typeof window.gtag === "function" &&
    typeof window.dataLayer !== "undefined"
  );
};

/**
 * Google Analytics 초기화 상태를 확인합니다.
 */
export const checkGAStatus = () => {
  console.log("=== Google Analytics 상태 확인 ===");
  console.log("window 존재:", typeof window !== "undefined");
  console.log("gtag 함수 존재:", typeof window?.gtag === "function");
  console.log("dataLayer 존재:", typeof window?.dataLayer !== "undefined");
  console.log("dataLayer 내용:", window?.dataLayer);
  console.log("추적 ID:", GA_TRACKING_ID);
  console.log("현재 URL:", window?.location?.href);
  console.log("User Agent:", navigator?.userAgent);
  console.log("쿠키 활성화:", navigator?.cookieEnabled);
  console.log("=====================================");
};

/**
 * 페이지뷰를 기록합니다.
 * @param pagePath 페이지 경로 (선택사항)
 * @param pageTitle 페이지 제목 (선택사항)
 */
export const trackPageView = (pagePath?: string, pageTitle?: string) => {
  try {
    if (isGtagLoaded()) {
      const params: any = {
        send_page_view: true,
      };
      if (pagePath) params.page_path = pagePath;
      if (pageTitle) params.page_title = pageTitle;

      window.gtag("config", GA_TRACKING_ID, params);

      // 강제로 이벤트도 전송
      window.gtag("event", "page_view", {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath || window.location.pathname,
      });
    } else {
      console.warn("GA pageview tracking failed: gtag not loaded");
    }
  } catch (error) {
    console.error("GA PageView tracking failed:", error);
  }
};

/**
 * Google Analytics 이벤트를 기록합니다.
 * @param eventName 이벤트 이름
 * @param parameters 이벤트 매개변수
 */
export const trackEvent = (eventName: string, parameters?: GAEventParams) => {
  try {
    if (isGtagLoaded()) {
      // 기본 파라미터 추가
      const eventParams = {
        ...parameters,
        timestamp: Date.now(),
        page_location: window.location.href,
        page_path: window.location.pathname,
      };

      window.gtag("event", eventName, eventParams);
    } else {
      console.warn(
        `GA event tracking failed: gtag not loaded. Event: ${eventName}`,
        parameters
      );
    }
  } catch (error) {
    console.error("GA Event tracking failed:", error);
  }
};

/**
 * 버튼 클릭 이벤트를 기록합니다.
 * @param buttonName 버튼 이름
 * @param category 카테고리 (기본값: 'button')
 * @param label 라벨 (선택사항)
 */
export const trackButtonClick = (
  buttonName: string,
  category: string = "button",
  label?: string
) => {
  trackEvent("click", {
    event_category: category,
    event_label: label || buttonName,
    value: 1,
  });
};

/**
 * 앱 다운로드 링크 클릭을 기록합니다.
 * @param platform 플랫폼 ('app_store' 또는 'google_play')
 */
export const trackAppDownload = (platform: "app_store" | "play_store") => {
  trackEvent("app_download_click", {
    event_category: "engagement",
    event_label: platform,
    platform: platform,
    value: 1,
  });
};

/**
 * 외부 링크 클릭을 기록합니다.
 * @param url 링크 URL
 * @param linkName 링크 이름
 */
export const trackExternalLink = (url: string, linkName?: string) => {
  trackEvent("external_link_click", {
    event_category: "navigation",
    event_label: linkName || url,
    link_url: url,
    value: 1,
  });
};

/**
 * 폼 제출을 기록합니다.
 * @param formName 폼 이름
 * @param success 성공 여부
 */
export const trackFormSubmit = (formName: string, success: boolean = true) => {
  trackEvent("form_submit", {
    event_category: "form",
    event_label: formName,
    form_name: formName,
    success: success,
    value: success ? 1 : 0,
  });
};

/**
 * 테스트용 이벤트를 즉시 전송합니다.
 * 개발자 도구 콘솔에서 호출하여 GA 작동 상태를 확인할 수 있습니다.
 */
export const sendTestEvent = () => {
  console.log("=== GA 테스트 이벤트 전송 ===");
  checkGAStatus();

  if (!isGtagLoaded()) {
    console.error("❌ gtag이 로드되지 않았습니다!");
    return;
  }

  // 여러 테스트 이벤트 전송
  const timestamp = new Date().toISOString();

  // 1. 기본 테스트 이벤트
  trackEvent("test_event", {
    event_category: "test",
    event_label: "manual_test",
    value: 1,
    custom_parameter_1: "test_value",
    timestamp: timestamp,
  });

  // 2. 페이지뷰 강제 전송
  window.gtag("event", "page_view", {
    page_title: "Test Page View",
    page_location: window.location.href,
    page_path: window.location.pathname,
    custom_parameter: "forced_pageview",
  });

  // 3. 클릭 이벤트 시뮬레이션
  window.gtag("event", "click", {
    event_category: "test",
    event_label: "test_button_click",
    value: 1,
  });

  console.log("✅ 테스트 이벤트 전송 완료");
  console.log("📊 구글 애널리틱스 실시간 보고서에서 확인해보세요.");
  console.log("🔍 개발자 도구 Network 탭에서 'collect' 요청 확인");
  console.log("==========================");
};

/**
 * 네트워크 요청 디버깅을 위한 함수
 */
export const debugNetworkRequests = () => {
  console.log("=== GA 네트워크 디버깅 ===");
  console.log("개발자 도구 → Network 탭에서 다음을 확인하세요:");
  console.log("1. 'collect' 또는 'google-analytics' 필터링");
  console.log("2. Status 200 응답 확인");
  console.log("3. Request Payload 내용 확인");
  console.log("=========================");

  // 테스트 이벤트 전송 후 네트워크 확인 안내
  sendTestEvent();
};

// 전역 함수로 등록하여 개발자 도구에서 쉽게 접근 가능하게 함
if (typeof window !== "undefined") {
  (window as any).sendTestEvent = sendTestEvent;
  (window as any).checkGAStatus = checkGAStatus;
  (window as any).debugNetworkRequests = debugNetworkRequests;
}
